import { useState } from 'react'
import type { CaseEntry, CaseEntryType, CaseEntryUnit } from '../../../types/caseEntry'

/** Hvilken knap formularen er åbnet fra — afgør hvilke felter der vises. */
export type EntryKind = 'hours' | 'material' | 'travel' | 'expense'

export const travelTypes = ['Kørsel', 'Servicevogn', 'Værktøj'] as const
export type TravelType = (typeof travelTypes)[number]

export const expenseTypes = ['Maskine', 'Bortskaffelse', 'Andet'] as const
export type ExpenseType = (typeof expenseTypes)[number]

const expenseEntryType: Record<ExpenseType, CaseEntryType> = {
  Maskine: 'Maskiner',
  Bortskaffelse: 'Bortskaffelse',
  Andet: 'Andet',
}

export interface EntryFormState {
  subtype: string
  date: string
  description: string
  quantity: string
  unit: CaseEntryUnit
  unitPrice: string
  vatRate: string
}

export function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/** Accepterer både dansk komma og punktum: "3,5" → 3.5 */
export function parseAmount(value: string): number {
  const cleaned = value.replace(/\s/g, '').replace(/\./g, (match, _offset, full: string) =>
    // "1.575,50" → tusindtalsseparator fjernes, når der også er komma
    full.includes(',') ? '' : match,
  )
  const number = Number(cleaned.replace(',', '.'))
  return Number.isFinite(number) ? number : NaN
}

export function toInput(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return ''
  return String(value).replace('.', ',')
}

export function kindOfEntry(entry: CaseEntry): EntryKind {
  if (entry.type === 'Timer') return 'hours'
  if (entry.type === 'Materialer') return 'material'
  if (entry.type === 'Kørsel' || entry.type === 'Servicevogn og værktøj') return 'travel'
  return 'expense'
}

const servicePrefix = /^(Servicevogn|Værktøj):\s*/

function initialState(kind: EntryKind, defaultHourlyRate: string, entry?: CaseEntry): EntryFormState {
  if (entry) {
    let subtype = ''
    let description = entry.description
    if (entry.type === 'Kørsel') subtype = 'Kørsel'
    if (entry.type === 'Servicevogn og værktøj') {
      const match = description.match(servicePrefix)
      subtype = match ? match[1] : 'Servicevogn'
      description = description.replace(servicePrefix, '')
    }
    if (kind === 'expense') {
      subtype = entry.type === 'Maskiner' ? 'Maskine' : entry.type
    }
    return {
      subtype,
      date: entry.date || todayIso(),
      description,
      quantity: toInput(entry.quantity),
      unit: entry.unit,
      unitPrice: toInput(entry.unitPrice),
      vatRate: String(entry.vatRate ?? 25),
    }
  }
  return {
    subtype: kind === 'travel' ? 'Kørsel' : kind === 'expense' ? 'Maskine' : '',
    date: todayIso(),
    description: '',
    quantity: kind === 'travel' || kind === 'expense' ? '1' : '',
    unit: kind === 'hours' ? 'timer' : kind === 'travel' ? 'km' : 'stk.',
    unitPrice: kind === 'hours' ? defaultHourlyRate.replace('.', ',') : '',
    vatRate: '25',
  }
}

/**
 * Lille, målrettet formular: knappen afgør typen, så der aldrig skal vælges
 * kategori først. Validerer og bygger en færdig linje til sagen.
 */
export function useCaseEntryForm(kind: EntryKind, defaultHourlyRate: string, editing?: CaseEntry) {
  const [form, setForm] = useState<EntryFormState>(() => initialState(kind, defaultHourlyRate, editing))
  const [formError, setFormError] = useState('')

  function setField<K extends keyof EntryFormState>(key: K, value: EntryFormState[K]) {
    setForm((current) => {
      const next = { ...current, [key]: value }
      // Kørsel er i km; servicevogn og værktøj i stk.
      if (kind === 'travel' && key === 'subtype') next.unit = value === 'Kørsel' ? 'km' : 'stk.'
      return next
    })
    setFormError('')
  }

  function entryType(): CaseEntryType {
    if (kind === 'hours') return 'Timer'
    if (kind === 'material') return 'Materialer'
    if (kind === 'travel') return form.subtype === 'Kørsel' ? 'Kørsel' : 'Servicevogn og værktøj'
    return expenseEntryType[form.subtype as ExpenseType] ?? 'Andet'
  }

  /** Validerer formularen og returnerer en færdig linje — eller null ved fejl. */
  function build(): CaseEntry | null {
    const quantity = parseAmount(form.quantity || '1')
    const unitPrice = parseAmount(form.unitPrice)

    if (!form.description.trim()) {
      setFormError('Skriv en kort beskrivelse.')
      return null
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setFormError(kind === 'hours' ? 'Angiv antal timer, fx 3,5.' : 'Angiv et antal større end 0.')
      return null
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      setFormError('Angiv en gyldig pris.')
      return null
    }

    const type = entryType()
    const description =
      type === 'Servicevogn og værktøj' ? `${form.subtype}: ${form.description.trim()}` : form.description.trim()
    const now = new Date().toISOString()

    return {
      id: editing?.id ?? `entry-${Math.random().toString(36).slice(2, 10)}`,
      type,
      date: form.date || todayIso(),
      description,
      quantity,
      unit: kind === 'hours' ? 'timer' : form.unit,
      unitPrice,
      // Ældre linjer beholder deres indkøbspris, men feltet vises ikke længere.
      ...(editing?.purchasePrice !== undefined ? { purchasePrice: editing.purchasePrice } : {}),
      vatRate: kind === 'material' ? Number(form.vatRate) || 0 : editing?.vatRate ?? 25,
      ...(editing?.quoteLineId ? { quoteLineId: editing.quoteLineId } : {}),
      createdAt: editing?.createdAt ?? now,
      updatedAt: now,
    }
  }

  return { form, setField, build, formError }
}
